import { useCallback, useEffect, useRef, useState } from "react";

import { BackendError } from "@/lib/backend";
import {
  getDatabookJobStatus,
  runDatabook,
  retryDatabookJob,
  type DatabookJobStatus,
} from "@/lib/api/databook";

import type { DatabookGenerationState } from "./DatabookGenerationStage";

interface UseBuilderDatabookOptions {
  projectId?: string;
  enabled: boolean;
  matchSelection: boolean;
  canStart: boolean;
  basisSelectionLoading: boolean;
  builderContextId: string | null;
  sourceRevisionId: string | null;
  legalStructureRevisionId: string | null;
  legalStructureFingerprint: string | null;
  basisId: string | null;
  financialYearEndMonth: number;
}

interface UseBuilderDatabookResult {
  state: DatabookGenerationState;
  flowDetected: boolean;
  start: () => Promise<void>;
  retry: (jobId: string) => Promise<void>;
}

const POLL_INTERVAL_MS = 2000;
const AMBIGUOUS_START_MISSING_JOB_LIMIT = 5;

function waitForPoll(): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, POLL_INTERVAL_MS);
  });
}

function jobMatchesSelection(
  job: DatabookJobStatus,
  builderContextId: string,
  basisId: string,
): boolean {
  return (
    job.builder_context_id === builderContextId
    && job.basis_id === basisId
  );
}

export function generationState(job: DatabookJobStatus): DatabookGenerationState {
  if (job.state === "ready") return { status: "ready", outcome: job };
  if (job.state === "failed") return { status: "failed", outcome: job };
  if (job.state === "queued") return { status: "queued" };
  return {
    status: "running",
    completedSteps: job.progress?.completed ?? 0,
    totalSteps: job.progress?.total ?? 0,
  };
}

export function databookJobAction(
  matchesSourcePlan: boolean,
  forceStart: boolean,
  currentState?: DatabookJobStatus["state"],
): "monitor" | "start" | "not_started" {
  if (!matchesSourcePlan && !forceStart) return "not_started";
  if (!matchesSourcePlan) return "start";
  if (
    forceStart
    && currentState !== "queued"
    && currentState !== "running"
  ) {
    return "start";
  }
  return "monitor";
}

export function isAmbiguousDatabookStartError(error: unknown): boolean {
  return (
    !(error instanceof BackendError)
    || error.status === 409
    || error.status === 429
    || error.status >= 500
  );
}

export function useBuilderDatabook({
  projectId,
  enabled,
  matchSelection,
  canStart,
  basisSelectionLoading,
  builderContextId,
  sourceRevisionId,
  legalStructureRevisionId,
  legalStructureFingerprint,
  basisId,
  financialYearEndMonth,
}: UseBuilderDatabookOptions): UseBuilderDatabookResult {
  const [state, setState] = useState<DatabookGenerationState>(() => ({
    status: projectId && basisSelectionLoading ? "checking" : "not_started",
  }));
  const [flowDetected, setFlowDetected] = useState(false);
  const flowRunRef = useRef(0);
  const explicitRunRef = useRef(false);
  useEffect(() => () => { flowRunRef.current += 1; }, [projectId]);
  const canStartRef = useRef(canStart);
  canStartRef.current = canStart;

  const monitorDatabookJob = useCallback(async (
    runId: number,
    forceStart: boolean,
  ): Promise<void> => {
    if (!projectId || flowRunRef.current !== runId) return;
    const matchesJob = (job: DatabookJobStatus) => !matchSelection && !forceStart
      ? true
      : Boolean(builderContextId && basisId && jobMatchesSelection(job, builderContextId, basisId));
    const loadCurrentJob = async (): Promise<DatabookJobStatus | null> => {
      while (flowRunRef.current === runId) {
        try {
          return await getDatabookJobStatus(projectId);
        } catch (error) {
          if (error instanceof BackendError && error.status === 404) return null;
          if (flowRunRef.current !== runId) return null;
          setFlowDetected(true);
          setState({ status: "reconnecting" });
          await waitForPoll();
        }
      }
      return null;
    };
    let currentJob: DatabookJobStatus | null = null;
    currentJob = await loadCurrentJob();
    if (flowRunRef.current !== runId) return;

    const matchesSelection = currentJob ? matchesJob(currentJob) : false;
    const jobAction = databookJobAction(
      matchesSelection,
      forceStart,
      currentJob?.state,
    );
    if (jobAction === "not_started") {
      setFlowDetected(false);
      setState({ status: "not_started" });
      return;
    }
    setFlowDetected(true);
    let ambiguousStart = false;
    if (jobAction === "start") {
      // Preparation can change while the job-status request is in flight.
      if (!canStartRef.current || !sourceRevisionId || !legalStructureRevisionId
        || !legalStructureFingerprint || !basisId) {
        setFlowDetected(false);
        setState({ status: "not_started" });
        return;
      }
      setState({ status: "queued" });
      try {
        await runDatabook(projectId, {
          financial_year_end_month: financialYearEndMonth,
          source_revision_id: sourceRevisionId,
          legal_structure_revision_id: legalStructureRevisionId,
          legal_structure_fingerprint: legalStructureFingerprint,
          basis_id: basisId,
        });
      } catch (error) {
        if (!isAmbiguousDatabookStartError(error)) {
          setState({
            status: "start_failed",
          });
          return;
        }
        ambiguousStart = true;
        setState({ status: "reconnecting" });
      }
      currentJob = null;
    }

    let missingJobChecks = 0;
    while (flowRunRef.current === runId) {
      if (currentJob === null) {
        await waitForPoll();
        currentJob = await loadCurrentJob();
        if (flowRunRef.current !== runId) return;
      }
      if (
        currentJob === null
        || !matchesJob(currentJob)
      ) {
        if (ambiguousStart) {
          missingJobChecks += 1;
          if (missingJobChecks >= AMBIGUOUS_START_MISSING_JOB_LIMIT) {
            setState({
              status: "start_failed",
            });
            return;
          }
        }
        await waitForPoll();
        currentJob = null;
        continue;
      }
      ambiguousStart = false;
      setState(generationState(currentJob));
      if (
        currentJob.state === "ready"
        || currentJob.state === "failed"
      ) {
        return;
      }
      await waitForPoll();
      currentJob = null;
    }
  }, [
    matchSelection,
    basisId,
    builderContextId,
    financialYearEndMonth,
    legalStructureFingerprint,
    legalStructureRevisionId,
    projectId,
    sourceRevisionId,
  ]);

  const start = useCallback(async (): Promise<void> => {
    if (
      !projectId
      || !canStart
      || !builderContextId
      || !sourceRevisionId
      || !legalStructureRevisionId
      || !legalStructureFingerprint
      || !basisId
    ) return;
    explicitRunRef.current = true;
    const runId = flowRunRef.current + 1;
    flowRunRef.current = runId;
    setFlowDetected(true);
    setState({ status: "queued" });
    try {
      await monitorDatabookJob(runId, true);
    } catch {
      if (flowRunRef.current !== runId) return;
      setState({
        status: "start_failed",
      });
    } finally {
      explicitRunRef.current = false;
    }
  }, [
    basisId,
    builderContextId,
    canStart,
    legalStructureFingerprint,
    legalStructureRevisionId,
    monitorDatabookJob,
    projectId,
    sourceRevisionId,
  ]);

  useEffect(() => {
    if (explicitRunRef.current) return;
    if (!enabled || !projectId) {
      flowRunRef.current += 1;
      setFlowDetected(false);
      setState({ status: "not_started" });
      return;
    }
    if (matchSelection && basisSelectionLoading) {
      flowRunRef.current += 1;
      setFlowDetected(false);
      setState({ status: "checking" });
      return;
    }
    if (matchSelection && (
      !sourceRevisionId
      || !legalStructureRevisionId
      || !legalStructureFingerprint
      || !basisId
    )) {
      flowRunRef.current += 1;
      setFlowDetected(false);
      setState({ status: "not_started" });
      return;
    }
    const runId = flowRunRef.current + 1;
    flowRunRef.current = runId;
    setState({ status: "checking" });
    monitorDatabookJob(runId, false).catch(() => {
      if (flowRunRef.current !== runId) return;
      setState({ status: "reconnecting" });
    });
    return () => {
      if (!explicitRunRef.current && flowRunRef.current === runId) {
        flowRunRef.current += 1;
      }
    };
  }, [
    enabled,
    matchSelection,
    basisId,
    basisSelectionLoading,
    legalStructureFingerprint,
    legalStructureRevisionId,
    monitorDatabookJob,
    projectId,
    sourceRevisionId,
  ]);

  const retryInFlight = useRef(false);
  const retry = useCallback(async (jobId: string) => {
    if (!projectId || !canStart || retryInFlight.current) return;
    retryInFlight.current = true;
    explicitRunRef.current = true;
    try {
      await retryDatabookJob(projectId, jobId);
      const runId = ++flowRunRef.current;
      setState({ status: "queued" });
      await monitorDatabookJob(runId, false);
    } finally {
      retryInFlight.current = false;
      explicitRunRef.current = false;
    }
  }, [projectId, canStart, monitorDatabookJob]);

  return {
    state,
    flowDetected,
    start,
    retry,
  };
}
